'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import HeroImageOverlay from './HeroImageOverlay';

import { trackBehavior } from '@/lib/analytics';

interface HeroContentProps {
  lang?: 'en' | 'de';
}

export default function HeroContent({ lang = 'en' }: HeroContentProps) {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(lang);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    // Update language if prop changes
    setCurrentLanguage(lang);

    const handleLanguageChange = (event: CustomEvent<{ language: 'en' | 'de' }>) => {
      setCurrentLanguage(event.detail.language);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, [lang]);

  const handleContactClick = () => {
    trackBehavior('cta_click', {
      cta_location: 'hero',
      cta_text: currentLanguage === 'en' ? 'Tell us what you\'re building →' : 'Erzählen Sie uns, was Sie bauen →',
      page: 'home'
    });
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
  {
    icon: '🧭',
    iconAlt: 'Compass emoji representing navigation and strategic direction',
    nameEn: 'Vision Sprint',
    nameDe: 'Vision Sprint',
    descEn: 'Define your product vision, target audience and growth metrics. Receive a vision document, roadmap and KPI framework, plus workshops and support.',
    descDe: 'Definieren Sie Ihre Produktvision, Zielgruppe und Wachstumskennzahlen. Erhalten Sie ein Visionsdokument, eine Roadmap und ein KPI-Framework sowie Workshops und Unterstützung.'
  },
  {
    icon: '🚀',
    iconAlt: 'Rocket emoji representing launch and performance acceleration',
    nameEn: 'Performance LaunchPad',
    nameDe: 'Performance LaunchPad',
    descEn: 'Design campaign architecture, configure tracking and analytics, prepare creative briefings and starter ad sets. Includes four weeks of optimisation to refine targeting, creatives and bids.',
    descDe: 'Entwerfen Sie Kampagnenarchitektur, konfigurieren Sie Tracking und Analysen, bereiten Sie kreative Briefings und Starter-Anzeigensets vor. Beinhaltet vier Wochen Optimierung zur Verfeinerung von Targeting, Creatives und Geboten.'
  },
  {
    icon: '📈',
    iconAlt: 'Chart increasing emoji representing continuous growth and optimization',
    nameEn: 'Growth Retainer',
    nameDe: 'Growth Retainer',
    descEn: 'Monthly service for continuous improvement. Review KPIs and analytics, optimise campaigns, refine creative assets and adjust strategy with weekly touchpoints.',
    descDe: 'Monatlicher Service für kontinuierliche Verbesserung. Überprüfen Sie KPIs und Analysen, optimieren Sie Kampagnen, verfeinern Sie kreative Assets und passen Sie die Strategie mit wöchentlichen Kontaktpunkten an.'
  },
  {
    icon: '🤖',
    iconAlt: 'Robot emoji representing AI automation and workflow integration',
    nameEn: 'AI Workflow Kickstart',
    nameDe: 'AI Workflow Kickstart',
    descEn: 'Integrate AI into your marketing. Define strategy, streamline processes, deploy custom AI agents and set up an AI‑driven toolchain and content calendar.',
    descDe: 'Integrieren Sie KI in Ihr Marketing. Definieren Sie Strategie, optimieren Sie Prozesse, setzen Sie benutzerdefinierte KI-Agenten ein und richten Sie eine KI-gesteuerte Toolchain und einen Inhaltskalender ein.'
  },
  {
    icon: '⚡',
    iconAlt: 'High voltage emoji representing energy and content generation power',
    nameEn: 'AI Content Engine',
    nameDe: 'AI Content Engine',
    descEn: 'Monthly production of professionally reviewed AI‑generated content. Videos, custom image assets and written content that feeds your AEO, GEO, and SEO growth strategies as well as your brand narratives and performance campaigns.',
    descDe: 'Monatliche Produktion professionell überprüfter KI-generierter Inhalte. Videos, benutzerdefinierte Bild-Assets und schriftliche Inhalte, die Ihre AEO-, GEO- und SEO-Wachstumsstrategien sowie Ihre Markennarrative und Performance-Kampagnen unterstützen.',
  },
  {
    icon: '🌐',
    iconAlt: 'Globe with meridians emoji representing web presence and global reach',
    nameEn: 'Web Presence Builder',
    nameDe: 'Web Presence Builder',
    descEn: 'Design and build a polished, responsive website with UX/UI design, content structuring, performance optimisation and on‑page SEO.',
    descDe: 'Entwerfen und erstellen Sie eine ausgefeilte, responsive Website mit UX/UI-Design, Inhaltsstrukturierung, Leistungsoptimierung und On-Page-SEO.'
  },
  {
    icon: '🧩',
    iconAlt: 'Puzzle piece emoji representing modular consulting and custom solutions',
    nameEn: 'Custom Consulting & Add‑Ons',
    nameDe: 'Custom Consulting & Add‑Ons',
    descEn: 'Flexible support for tailored problem solving, product development, strategic planning, research, ad creation, landing pages, conversion‑rate optimisation, ASO, analytics dashboards and ongoing campaign adjustments.',
    descDe: 'Flexible Unterstützung für maßgeschneiderte Problemlösungen, Produktentwicklung, strategische Planung, Recherche, Anzeigenerstellung, Landing Pages, Conversion-Rate-Optimierung, ASO, Analyse-Dashboards und laufende Kampagnenanpassungen.'
  },
  {
    icon: '✨',
    iconAlt: 'Sparkles emoji representing wildcard creativity and innovative solutions',
    nameEn: 'Wildcard Deal',
    nameDe: 'Wildcard Deal',
    descEn: 'Surprising solutions for extraordinary challenges. Explore unconventional thinking and innovative approaches beyond your imagination if you are stuck or need to overcome conflicts.',
    descDe: 'Überraschende Lösungen für außergewöhnliche Herausforderungen. Unkonventionelle Ansätze, wenn Sie feststecken oder frische Ideen brauchen (Preise auf Anfrage).'
  }];

  const whyWorkWithUs = [
  {
    en: 'By collaborating with us you will tap into our professional and objective outside perspective that can uncover opportunities and evoke creative problem solving you might otherwise miss out on by staying just within your own organization.',
    de: 'Durch die Zusammenarbeit mit uns nutzen Sie unseren professionellen und objektiven Blick von außen, der Chancen aufdeckt und kreative Problemlösungen hervorruft, die Ihnen ansonsten entgehen könnten, wenn Sie nur innerhalb Ihrer eigenen Organisation bleiben.'
  },
  {
    en: 'We provide strategic marketing and product growth expertise across B2C and B2B business models along with an AI focused approach for accelerating research, ideation and content creation.',
    de: 'Wir bieten strategische Marketing- und Produktwachstums‑Expertise über B2C‑ und B2B‑Geschäftsmodelle hinweg sowie einen KI‑fokussierten Ansatz zur Beschleunigung von Recherche, Ideenfindung und Inhaltserstellung.'
  },
  {
    en: 'We help you build your website, software or app project and bring it to the market.',
    de: 'Wir helfen Ihnen, Ihre Website, Ihre Software oder Ihr App‑Projekt zu entwickeln und auf den Markt zu bringen.'
  },
  {
    en: 'We specialize in leveraging AI prompting and system building while safeguarding quality and brand integrity.',
    de: 'Wir sind darauf spezialisiert, KI‑Prompting und Systemaufbau zu nutzen und gleichzeitig Qualität und Markenintegrität zu gewährleisten.'
  },
  {
    en: 'Contact us if you are looking for AI and marketing expertise, product marketing solutions, performance campaigns, brand awareness and breakthrough improvements that drive your core business metrics to a higher level.',
    de: 'Kontaktieren Sie uns, wenn Sie nach KI- und Marketing-Expertise, Produktmarketing‑Lösungen, Performance‑Kampagnen, Markenbekanntheit und bahnbrechenden Verbesserungen suchen, die Ihre wichtigsten Geschäftszahlen auf ein höheres Niveau heben.'
  }];

  const valuePillars = [
    {
      icon: '🧭',
      titleEn: 'Strategy & Vision',
      titleDe: 'Strategie & Vision',
      descEn: 'AI roadmaps, product positioning, and go-to-market playbooks. We map how and where you\'re going. With AI speed and brand integrity.',
      descDe: 'KI-Roadmaps, Produktpositionierung und Go-to-Market-Playbooks. Wir planen, wie und wohin Sie gehen. Mit KI-Geschwindigkeit und Markenintegrität.'
    },
    {
      icon: '⚙️',
      titleEn: 'Systems & Frameworks',
      titleDe: 'Systeme & Frameworks',
      descEn: 'Custom AI architectures, automated workflows, and systemic infrastructure. The building blocks that scale with your product.',
      descDe: 'Maßgeschneiderte KI-Architekturen, automatisierte Workflows und systemische Infrastruktur. Die Bausteine, die mit Ihrem Produkt skalieren.'
    },
    {
      icon: '🎯',
      titleEn: 'Brand & Authority',
      titleDe: 'Marke & Autorität',
      descEn: (
        <>
          Content engines, brand positioning, and thought leadership. Be the obvious choice in your category. Visible to AI and humans with{' '}
          <Link 
            href="https://planetpulsestudios.com/en/projects-section/ai-website-checker"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#16c55f] hover:text-[#00b8ff] underline decoration-[#16c55f]/50 hover:decoration-[#00b8ff] transition-colors duration-300 font-semibold"
          >
            AEO, GEO & SEO
          </Link>
          .
        </>
      ),
      descDe: (
        <>
          Content-Engines, Markenpositionierung und Thought Leadership. Seien Sie die offensichtliche Wahl in Ihrer Kategorie. Sichtbar für KI und Menschen mit{' '}
          <Link 
            href="https://planetpulsestudios.com/en/projects-section/ai-website-checker"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#16c55f] hover:text-[#00b8ff] underline decoration-[#16c55f]/50 hover:decoration-[#00b8ff] transition-colors duration-300 font-semibold"
          >
            AEO, GEO & SEO
          </Link>
          .
        </>
      )
    }
  ];

  return (
    <div className="relative z-10 flex flex-col items-center justify-center px-4 py-20 space-y-16">
      {/* NEW HERO LAYOUT */}
      
      {/* 1. HEADLINE */}
      <div className="text-center space-y-6 animate-fade-in max-w-5xl">
        <h1 
          className="text-4xl md:text-6xl lg:text-7xl font-bold text-center pt-[25px]" 
          style={{
            fontFamily: '"Exo 2", sans-serif',
            color: '#00b8ff'
          }}
          data-lang={currentLanguage}
        >
          {currentLanguage === 'en' ?'We Build AI Systems, Strategies & Brand Authority for SaaS, Tech & Start Ups' :'Wir bauen KI-Systeme, Strategien & Markenautorität für SaaS, Tech & Start-Ups'
          }
        </h1>
        
        {/* 2. SUBHEADLINE */}
        <div className="rounded-xl p-6 border border-[#16c55f]/20" style={{ backgroundColor: 'rgba(22, 197, 95, 0.05)' }}>
          <p 
            className="text-xl md:text-2xl text-center leading-relaxed" 
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#16c55f'
            }}
            data-lang={currentLanguage}
          >
            {currentLanguage === 'en' ?'From product vision to shipped systems, PlanetPulse Studios helps you grow faster with tailored AI solutions that actually work for your business.' :'Von der Produktvision bis zu ausgelieferten Systemen hilft Ihnen PlanetPulse Studios, schneller zu wachsen mit maßgeschneiderten KI-Lösungen, die tatsächlich für Ihr Unternehmen funktionieren.'
            }
          </p>
        </div>
        
        {/* 3. CTA BUTTON */}
        <div className="pt-4">
          <Link
            href={`/${currentLanguage}/contact-section`}
            prefetch={false}
            className="inline-block px-8 py-4 bg-[#00b8ff] hover:bg-[#16c55f] text-white font-semibold rounded-lg transition-colors duration-300 shadow-lg hover:shadow-xl text-lg"
            onClick={handleContactClick}
            data-lang={currentLanguage}
          >
            {currentLanguage === 'en' ?'Tell us what you\'re building →' :'Erzählen Sie uns, was Sie bauen →'
            }
          </Link>
        </div>
      </div>

      {/* 4. HERO IMAGE WITH CAPTION */}
      <div className="w-full max-w-6xl animate-fade-in space-y-4">
        <div 
          className={`relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-[#16c55f]/30 ${isHovering ? 'hero-image-hover' : 'hero-image-normal'}`}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onTouchStart={() => setIsHovering(true)}
          onTouchEnd={() => setIsHovering(false)}
        >
          <AppImage
            src="/assets/images/ai-is-killing-the-click-why-geo-is-the-best-marketing-strategy-in-2026.-1770177919874.jpg"
            alt="Business professionals collaborating with AI technology, representing the synergy of artificial and human intelligence in modern marketing"
            fill
            priority={true}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
            className="object-cover"
            quality={90}
          />
          <HeroImageOverlay />
        </div>
        
        {/* IMAGE CAPTION */}
        <p 
          className="text-center text-lg md:text-xl italic" 
          style={{
            fontFamily: '"Exo 2", sans-serif',
            color: '#00d9ff',
            fontWeight: 300
          }}
          data-lang={currentLanguage}
        >
          {currentLanguage === 'en' ?'Where AI meets Human Intelligence' :'Wo KI auf menschliche Intelligenz trifft - Beginnen Sie, Ergebnisse zu orchestrieren!'
          }
        </p>
      </div>

      {/* 5. THREE VALUE PILLARS */}
      <div className="w-full max-w-6xl animate-fade-in space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {valuePillars.map((pillar, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden rounded-xl border border-[#00b8ff]/30 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-[#16c55f]/50 p-6"
              style={{ 
                backgroundColor: 'rgba(0, 184, 255, 0.05)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <div className="space-y-4 text-center">
                <div className="text-5xl mb-4 filter drop-shadow-lg" style={{ color: '#16c55f' }}>
                  {pillar.icon}
                </div>
                <h4 
                  className="text-xl md:text-2xl font-bold" 
                  style={{ color: '#00b8ff' }}
                  data-lang={currentLanguage}
                >
                  {currentLanguage === 'en' ? pillar.titleEn : pillar.titleDe}
                </h4>
                <p 
                  className="text-foreground-secondary leading-relaxed"
                  data-lang={currentLanguage}
                >
                  {currentLanguage === 'en' ? pillar.descEn : pillar.descDe}
                </p>
              </div>
              {/* Glow effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
                background: 'radial-gradient(circle at center, rgba(22, 197, 95, 0.1) 0%, transparent 70%)'
              }}></div>
            </div>
          ))}
        </div>
      </div>

      {/* 6. TRUST LINE */}
      <div className="animate-fade-in">
        <div className="inline-block px-6 py-3 rounded-lg" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
          <p 
            className="text-center text-sm md:text-base"
            style={{
              color: '#16c55f',
              fontFamily: '"Exo 2", sans-serif'
            }}
            data-lang={currentLanguage}
          >
            {currentLanguage === 'en' ? '15+ years of marketing and product growth expertise · AI speed, human nuance · SaaS · Tech · B2B · Gaming · B2C': '15+ Jahre Marketing- und Produktwachstums-Expertise · KI-Geschwindigkeit, menschliche Nuancen · SaaS · Tech · B2B · Gaming · B2C'
            }
          </p>
        </div>
      </div>

      {/* SERVICE BULLETS SECTION - Restructured with Pillar Groups */}
      <div className="w-full max-w-6xl space-y-8 animate-fade-in">
        <h3 
          className="text-3xl md:text-5xl font-bold text-center mb-4" 
          style={{
            fontFamily: '"Exo 2", sans-serif',
            color: '#00b8ff'
          }}
          data-lang={currentLanguage}
        >
          {currentLanguage === 'en' ? 'A sneak peek into our services' : 'Eine kleine Vorschau unserer Dienstleistungen'}
        </h3>

        {/* Pillar Group 1: Strategy & Vision */}
        <div className="space-y-6">
          <div className="flex items-center justify-center gap-3">
            <span className="text-4xl">🧭</span>
            <h4 
              className="text-2xl md:text-3xl font-bold" 
              style={{ color: '#16c55f' }}
              data-lang={currentLanguage}
            >
              {currentLanguage === 'en' ? 'Strategy & Vision' : 'Strategie & Vision'}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Vision Sprint */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  🧭
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  Vision Sprint
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Define your product vision, target audience and growth metrics. Conduct AI leverage audit. Receive a vision document, roadmap and KPI framework, plus workshops and support.' :'Definieren Sie Ihre Produktvision, Zielgruppe und Wachstumskennzahlen. Führen Sie ein AI-Leverage-Audit durch. Erhalten Sie ein Visionsdokument, eine Roadmap und ein KPI-Framework sowie Workshops und Unterstützung.'
                  }
                </p>
              </div>
            </div>

            {/* Custom Consulting & Add-Ons */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  🧩
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ? 'Custom Consulting & Add‑Ons' : 'Custom Consulting & Add‑Ons'}
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Flexible support for tailored problem solving, product development, strategic planning, research, ad creation, landing pages, conversion‑rate optimisation, ASO, analytics dashboards and ongoing campaign adjustments.' :'Flexible Unterstützung für maßgeschneiderte Problemlösungen, Produktentwicklung, strategische Planung, Recherche, Anzeigenerstellung, Landing Pages, Conversion-Rate-Optimierung, ASO, Analyse-Dashboards und laufende Kampagnenanpassungen.'
                  }
                </p>
              </div>
            </div>

            {/* Wildcard Deal */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  ✨
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  Wildcard Deal
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Strategic problem-solving for complex, high-stakes challenges. When standard playbooks don\'t fit, we bring unconventional thinking and cross-domain expertise to find your breakthrough.'
                    : 'Strategische Problemlösung für komplexe, hochriskante Herausforderungen. Wenn Standardansätze nicht passen, bringen wir unkonventionelles Denken und domänenübergreifende Expertise ein, um Ihren Durchbruch zu finden.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pillar Group 2: Systems & Frameworks */}
        <div className="space-y-6">
          <div className="flex items-center justify-center gap-3">
            <span className="text-4xl">⚙️</span>
            <h4 
              className="text-2xl md:text-3xl font-bold" 
              style={{ color: '#16c55f' }}
              data-lang={currentLanguage}
            >
              {currentLanguage === 'en' ? 'Systems & Frameworks' : 'Systeme & Frameworks'}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* AI Workflow Kickstart */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  🤖
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  AI Workflow Kickstart
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Integrate AI into your marketing and operations. Define strategy, streamline processes, deploy custom AI agents and set up an AI‑driven toolchain and content calendar.' :'Integrieren Sie KI in Ihr Marketing und Ihre Abläufe. Definieren Sie Strategie, optimieren Sie Prozesse, setzen Sie benutzerdefinierte KI-Agenten ein und richten Sie eine KI-gesteuerte Toolchain und einen Inhaltskalender ein.'
                  }
                </p>
              </div>
            </div>

            {/* Web Presence Builder */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  🌐
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  Web Presence Builder
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Design and build a polished, responsive website with UX/UI design, content structuring, performance optimisation and on‑page SEO.' :'Entwerfen und erstellen Sie eine ausgefeilte, responsive Website mit UX/UI-Design, Inhaltsstrukturierung, Leistungsoptimierung und On-Page-SEO.'
                  }
                </p>
              </div>
            </div>

            {/* Performance LaunchPad */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  🚀
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  Performance LaunchPad
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Design campaign architecture, configure tracking and analytics, prepare creative briefings and starter ad sets. Includes four weeks of optimisation to refine targeting, creatives and bids.' :'Entwerfen Sie Kampagnenarchitektur, konfigurieren Sie Tracking und Analysen, bereiten Sie kreative Briefings und Starter-Anzeigensets vor. Beinhaltet vier Wochen Optimierung zur Verfeinerung von Targeting, Creatives und Geboten.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pillar Group 3: Brand & Authority */}
        <div className="space-y-6">
          <div className="flex items-center justify-center gap-3">
            <span className="text-4xl">🎯</span>
            <h4 
              className="text-2xl md:text-3xl font-bold" 
              style={{ color: '#16c55f' }}
              data-lang={currentLanguage}
            >
              {currentLanguage === 'en' ? 'Brand & Authority' : 'Marke & Autorität'}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* AI Content Engine */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  ⚡
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  AI Content Engine
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Monthly production of professionally reviewed AI‑generated content for optimal brand authority. Videos, custom image assets and written content that feeds your AEO, GEO, and SEO growth strategies as well as your brand narratives and performance campaigns.' :'Monatliche Produktion professionell überprüfter KI-generierter Inhalte für optimale Markenautorität. Videos, benutzerdefinierte Bild-Assets und schriftliche Inhalte, die Ihre AEO-, GEO- und SEO-Wachstumsstrategien sowie Ihre Markennarrative und Performance-Kampagnen unterstützen.'
                  }
                </p>
              </div>
            </div>

            {/* Growth Retainer */}
            <div className="service-card-animated group relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-2xl p-6 bg-black/40 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-xl p-2 flex items-center justify-center text-5xl" style={{ backgroundColor: '#00b8ff' }}>
                  📈
                </div>
                <h5 className="text-xl font-bold text-[#16c55f]" data-lang={currentLanguage}>
                  Growth Retainer
                </h5>
                <p className="text-foreground-secondary leading-relaxed" data-lang={currentLanguage}>
                  {currentLanguage === 'en' ?'Monthly service for continuous improvement. Review KPIs and analytics, optimise campaigns, refine creative assets and adjust strategy with weekly touchpoints.' :'Monatlicher Service für kontinuierliche Verbesserung. Überprüfen Sie KPIs und Analysen, optimieren Sie Kampagnen, verfeinern Sie kreative Assets und passen Sie die Strategie mit wöchentlichen Kontaktpunkten an.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <Link
            href="/services-section"
            prefetch={false}
            className="px-8 py-4 rounded-lg font-semibold text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
            style={{ backgroundColor: '#00b8ff' }}
            data-lang={currentLanguage}
          >
            {currentLanguage === 'en' ? 'Check Our Services' : 'Unsere Dienstleistungen ansehen'}
          </Link>
        </div>
      </div>

      {/* Why Work With Us Section - Rewritten */}
      <div className="w-full max-w-4xl space-y-8 animate-fade-in">
        <h3 
          className="text-3xl md:text-5xl font-bold text-center mb-4" 
          style={{
            fontFamily: '"Exo 2", sans-serif',
            color: '#00b8ff'
          }}
          data-lang={currentLanguage}
        >
          {currentLanguage === 'en' ? 'Why working with us is a good idea' : 'Warum es eine gute Idee ist, mit uns zu arbeiten'}
        </h3>
        <div className="space-y-6 rounded-xl p-8 border border-[#00b8ff]/20 bg-black/40 backdrop-blur-sm">
          <p className="text-lg text-foreground-secondary leading-relaxed text-center" data-lang={currentLanguage}>
            {currentLanguage === 'en' ? "We're not a traditional agency and we're not just consultants. We do both, hands on. Combining strategy with execution, under one roof." :"Wir sind keine traditionelle Agentur und wir sind nicht nur Berater. Wir machen beides, praktisch. Wir kombinieren Strategie mit Umsetzung, unter einem Dach."
            }
          </p>
          <p className="text-lg text-foreground-secondary leading-relaxed text-center" data-lang={currentLanguage}>
            {currentLanguage === 'en' ? "You get 15+ years of marketing and product growth expertise combined with tailored AI systems that actually boost your business. We bring an objective outside perspective that uncovers opportunities you can't see from inside your own organisation. We build the solution together with you." :"Sie erhalten 15+ Jahre Marketing- und Produktwachstums-Expertise kombiniert mit KI-Systemen, die tatsächlich ausgeliefert werden. Wir bringen eine objektive Außenperspektive, die Chancen aufdeckt, die Sie von innerhalb Ihrer eigenen Organisation nicht sehen können. Dann bauen wir die Lösung mit Ihnen."
            }
          </p>
          <p className="text-lg text-foreground-secondary leading-relaxed text-center" data-lang={currentLanguage}>
            {currentLanguage === 'en'
              ? "Whether you need a go-to-market playbook, custom AI workflows, or a content engine that feeds your brand authority, we design it, build it and help your team run it." :"Ob Sie ein Go-to-Market-Playbook, maßgeschneiderte KI-Workflows oder eine Content-Engine benötigen, die Ihre Markenautorität stärkt, wir entwerfen es, bauen es und helfen Ihrem Team, es zu betreiben."
            }
          </p>
          <p className="text-lg text-foreground-secondary leading-relaxed text-center" data-lang={currentLanguage}>
            {currentLanguage === 'en' ? "AI never acts alone. Every critical decision passes through human expertise. That's our promise." :"KI handelt nie allein. Jede kritische Entscheidung durchläuft menschliche Expertise. Das ist unser Versprechen."
            }
          </p>
        </div>
      </div>

      {/* Contact Button */}
      <div className="animate-fade-in">
        <Link
          href={`/${currentLanguage}/contact-section`}
          prefetch={false}
          className="inline-block px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
          style={{
            fontFamily: '"Exo 2", sans-serif',
            backgroundColor: 'transparent',
            border: '2px solid #00b8ff',
            color: '#00b8ff'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#00b8ff';
            e.currentTarget.style.color = '#031b3a';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#00b8ff';
            e.currentTarget.style.boxShadow = 'none';
          }}
          data-lang={currentLanguage}
          onClick={() => {
            trackBehavior('cta_click', {
              cta_location: 'hero_bottom',
              cta_text: currentLanguage === 'en' ? 'Contact Us' : 'Kontaktieren Sie uns',
              page: 'home'
            });
          }}
        >
          {currentLanguage === 'en' ? 'Contact Us' : 'Kontaktieren Sie uns'}
        </Link>
      </div>
    </div>);

}